import { type SanityBlogPostPage } from '@data/sanity/queries/types/blog'
import { type SanityBlogPostBody } from '@data/sanity/queries/types/modules'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getPortableTextHtml } from './portable-text'
import { type BlogPostSchema, type ImageSchema } from './types'
import { getSanityImageUrl } from '@lib/image'
import { type Locale } from '@lib/language'
import { getPageUrl } from '@lib/routes'

export const getBlogPostSchema = (
  locale: Locale,
  blogPost: SanityBlogPostPage,
  site: SanitySiteFragment
) => {
  const {
    title: headline,
    excerpt: abstract,
    slug,
    author,
    date,
    modifiedDate,
    categories,
    coverImage,
    modules,
    seo,
  } = blogPost
  const domain = site.settings.siteURL.replace(/\/+$/g, '')
  const blogPostUrl = getPageUrl(locale, 'blogPost', slug)

  const blogPostBodyModule = modules?.find(
    (module) => module._type == 'blogPostBody'
  ) as SanityBlogPostBody | undefined
  const articleBody = getPortableTextHtml(blogPostBodyModule?.content)

  const imageUrl = getSanityImageUrl(coverImage, {
    width: coverImage?.dimensions.width,
    height: coverImage?.dimensions.height,
    fitMax: true,
    autoFormat: true,
  })
  const thumbnailUrl = getSanityImageUrl(seo?.shareGraphic, {
    // Width and height taken from `HeadSeo` component
    width: 1200,
    height: 630,
  })

  const schema: BlogPostSchema = {
    '@type': 'BlogPosting',
    headline,
    abstract,
    articleBody,
    url: `${domain}${blogPostUrl}`,
    datePublished: new Date(date),
    dateModified: new Date(modifiedDate),
    keywords: categories?.map((category) => category.name),
    thumbnailUrl,
  }

  if (author) {
    const blogAuthorUrl = getPageUrl(locale, 'blogAuthor', author.slug)
    schema.author = {
      '@type': 'Person',
      name: author.name,
      url: `${domain}${blogAuthorUrl}`,
    }
  }

  if (imageUrl) {
    const imageWidth = coverImage?.dimensions.width
    const imageHeight = coverImage?.dimensions.height
    const image: ImageSchema = {
      '@type': 'ImageObject',
      '@id': imageUrl,
      url: imageUrl,
    }

    if (imageWidth) {
      image.width = `${imageWidth}`
    }
    if (imageHeight) {
      image.height = `${imageHeight}`
    }

    schema.image = image
  }

  return JSON.stringify(schema)
}
